import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  DollarSign,
  TrendingUp,
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  Phone,
  Mail,
  CreditCard,
  Users,
  BarChart3,
  Clock,
  Star
} from 'lucide-react';

export default function SellLeadsPage() {
  const navigate = useNavigate();

  const steps = [
    {
      number: "01",
      title: "Sign Up",
      description: "Create your free seller account in minutes. No upfront costs or commitments.",
      icon: Users
    },
    {
      number: "02",
      title: "Verify Your Email",
      description: "Confirm your email address to activate your account and access your dashboard.",
      icon: Mail
    },
    {
      number: "03",
      title: "Get Your API Key",
      description: "Generate your unique API key to start submitting leads programmatically.",
      icon: Zap
    },
    {
      number: "04",
      title: "Submit Leads",
      description: "Send us your quality leads via our simple API. Cold calls, live transfers, or data leads.",
      icon: TrendingUp
    },
    {
      number: "05",
      title: "Get Paid",
      description: "Watch your earnings grow! We pay via PayPal, Zelle, or check - your choice.",
      icon: DollarSign
    }
  ];

  const leadTypes = [
    {
      type: "Data Leads",
      description: "Contact information for potential customers ready to be contacted",
      icon: BarChart3,
      color: "from-blue-500 to-cyan-500"
    },
    {
      type: "Cold Call Leads",
      description: "Pre-qualified leads that have shown interest and are expecting a call",
      icon: Phone,
      color: "from-purple-500 to-pink-500"
    },
    {
      type: "Live Transfers",
      description: "Hot leads transferred directly to our clients in real-time",
      icon: Zap,
      color: "from-green-500 to-emerald-500"
    }
  ];

  const benefits = [
    "Competitive per-lead payouts",
    "Real-time tracking dashboard",
    "Fast payments - weekly or bi-weekly",
    "Multiple payment methods",
    "Dedicated support team",
    "No minimum volume requirements"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/50 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2">
              <img src="/images/vital-reach-media-10.png" alt="Vital Reach Media" className="h-10" />
            </a>
            <div className="flex items-center gap-4">
              <a href="/seller/login" className="text-gray-300 hover:text-white transition-colors">
                Seller Login
              </a>
              <Button 
                onClick={() => navigate('/seller/signup')}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              >
                Start Selling
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-full mb-6">
            <DollarSign className="w-4 h-4 text-green-400" />
            <span className="text-green-300 text-sm font-medium">Turn Your Leads Into Cash</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Sell Us Your{' '}
            <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
              Quality Leads
            </span>
          </h1>
          
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Partner with Vital Reach Media and get paid for every lead you send. 
            We buy cold calls, live transfers, and data leads at competitive rates.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg"
              onClick={() => navigate('/seller/signup')}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-lg px-8 py-6"
            >
              <DollarSign className="w-5 h-5 mr-2" />
              Start Earning Today
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
              className="border-white/20 text-white hover:bg-white/10 text-lg px-8 py-6"
            >
              Learn How It Works
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-gray-400">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-400" />
              <span>Secure Payments</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-400" />
              <span>Fast Payouts</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-green-400" />
              <span>Top Rates</span>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Types Section */}
      <section className="py-20 px-4 bg-black/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">What We Buy</h2>
            <p className="text-gray-400 text-lg">We accept multiple types of quality leads</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {leadTypes.map((item, index) => (
              <Card key={index} className="bg-white/5 border-white/10 overflow-hidden group hover:border-green-500/50 transition-all">
                <CardContent className="pt-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.type}</h3>
                  <p className="text-gray-400">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-gray-400 text-lg">Start earning in 5 simple steps</p>
          </div>
          
          <div className="relative">
            {/* Connection line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-green-500 via-emerald-500 to-transparent hidden lg:block" />
            
            <div className="space-y-12">
              {steps.map((step, index) => (
                <div key={index} className={`flex flex-col lg:flex-row items-center gap-8 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className="flex-1 text-center lg:text-left">
                    <div className="inline-flex items-center gap-3 mb-4">
                      <span className="text-5xl font-bold text-green-500/30">{step.number}</span>
                      <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                        <step.icon className="w-6 h-6 text-green-400" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{step.title}</h3>
                    <p className="text-gray-400 text-lg">{step.description}</p>
                  </div>
                  <div className="w-4 h-4 rounded-full bg-green-500 border-4 border-gray-900 hidden lg:block z-10" />
                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-green-900/20 to-emerald-900/20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Why Sell to Us?</h2>
            <p className="text-gray-400 text-lg">We make it easy and profitable</p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="text-gray-200">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Methods */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Get Paid Your Way</h2>
          <p className="text-gray-400 text-lg mb-12">Choose the payment method that works best for you</p>
          
          <div className="grid sm:grid-cols-3 gap-6">
            <Card className="bg-white/5 border-white/10 p-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                <CreditCard className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">PayPal</h3>
              <p className="text-gray-400">Fast and secure digital payments</p>
            </Card>
            
            <Card className="bg-white/5 border-white/10 p-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Zap className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Zelle</h3>
              <p className="text-gray-400">Instant bank transfers</p>
            </Card>
            
            <Card className="bg-white/5 border-white/10 p-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <Mail className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Check</h3>
              <p className="text-gray-400">Traditional mailed checks</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-r from-green-600 to-emerald-600 border-0 overflow-hidden relative">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTAtMThjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6bTE4IDBjMC0yLjIwOS0xLjc5MS00LTQtNHMtNCAxLjc5MS00IDQgMS43OTEgNCA0IDQgNC0xLjc5MSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
            <CardContent className="py-12 px-8 text-center relative z-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Ready to Start Earning?
              </h2>
              <p className="text-green-100 text-lg mb-8 max-w-2xl mx-auto">
                Join our network of lead sellers today. Sign up takes just 2 minutes 
                and you could be submitting leads by tomorrow.
              </p>
              <Button 
                size="lg"
                onClick={() => navigate('/seller/signup')}
                className="bg-white text-green-600 hover:bg-gray-100 text-lg px-8 py-6"
              >
                Create Your Seller Account
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-gray-400 text-sm">
          <p>© 2024 Vital Reach Media. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <a href="/seller/login" className="hover:text-white transition-colors">Seller Login</a>
            <a href="/login" className="hover:text-white transition-colors">Client Login</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
